import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, computed, signal, TemplateRef, ViewChild, Inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatTableModule } from "@angular/material/table";
import { MatSortModule } from "@angular/material/sort";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatDrawerContainer, MatDrawer, MatDrawerContent } from "@angular/material/sidenav";
import { MatSnackBar } from "@angular/material/snack-bar";

import { DistributionMaterialComponent } from "./distribution-material/distribution-material.component";
import { getCode } from "app/utils/method";
import { SalesDistributionService } from "app/modules/components/assistant/sales-distribution/sales-distribution.service";
import { UpdateOrderService } from "app/modules/components/sales/update-order/update-order.service";
import { DistributionGroupedByTrip, GroupedBySerial } from "../interfaces/distribution.interface";
import { StatusEnum } from '@albatrosdeveloper/ave-models-package/lib/enums';
import { MaterialService } from "app/modules/components/material/material/material.service";
import { HandlerMessageService } from "app/common/services/handler-error/handler-message.service";
import { LanguageService } from "app/core/i18n/language.service";
import { DetailUnitsLike, MaterialMasterLike, resolveEditableLogisticUnit } from "./logistic-unit.resolver";

@Component({
  selector: "app-modal-distribution",
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatTableModule,
    MatSortModule,
    MatTooltipModule,
    MatButtonModule,
    MatIconModule,
    MatDrawerContainer,
    MatDrawer,
    MatDrawerContent,
    DistributionMaterialComponent
  ],
  templateUrl: "./modal-distribution.component.html",
})
export class ModalDistributionComponent {
  getCode = getCode;

  // Señales
  public readonly deliveryGroupUnified = signal<GroupedBySerial[]>([]);
  public readonly deliveryGroupMaterialUnified = signal<DistributionGroupedByTrip[]>([]);
  public readonly expandedMaterial = signal<DistributionGroupedByTrip>(null);
  public readonly selectedDocument = signal<GroupedBySerial>(null);

  // Drawer
  drawerMode: "over" | "side" = "side";
  drawerOpened = false;
  childDrawerOpened = false;

  // Computed
  public readonly documentCount = computed(() => this.deliveryGroupUnified().length);
  public readonly isSaveDistribution = computed(() => {
    const allMaterials = this.deliveryGroupUnified().flatMap(doc => doc.items ?? []);
    const totalSpare = allMaterials.reduce((acc, mat) => acc + (mat.spare ?? 0), 0);
    const hasStatus6 = allMaterials.some(mat => mat.orderdetail?.some(item => item.documentStatusId === StatusEnum.ORDER_IN_PROGRESS));
    return totalSpare === 0 && hasStatus6;
  });

  @ViewChild("updateDialog") updateDialog!: TemplateRef<any>;
  dialogConEditRef!: MatDialogRef<any>;

  /** Maestros de material cargados una vez por apertura del wizard, por `material._id`. */
  private masters = new Map<string, MaterialMasterLike>();

  /**
   * models-package tipa `_id` como `mongoose.Types.ObjectId`, pero HTTP siempre lo serializa como
   * string. Este helper centraliza esa conversión para las claves del mapa de maestros.
   */
  private static materialIdOf(id: unknown): string | undefined {
    return id ? String(id) : undefined;
  }

  constructor(
    private salesDistributionService: SalesDistributionService,
    public dialogRef: MatDialogRef<ModalDistributionComponent>,
    private readonly updateOrderService: UpdateOrderService,
    private readonly materialService: MaterialService,
    private readonly handlerMessage: HandlerMessageService,
    private readonly languageService: LanguageService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      data: GroupedBySerial[];
      isSaveDistribution: boolean;
      tripId: number;
      UMPActive: boolean;
    }
  ) {}

  ngOnInit() {
    this.sortDocument(this.data.data);
    this.loadMaterialMasters();
  }

  /** Carga los maestros de material una sola vez por apertura, para resolver la unidad logística. */
  private loadMaterialMasters(): void {
    const materialIds = Array.from(
      new Set(
        (this.data.data ?? [])
          .flatMap(doc => doc.items ?? [])
          .map(item => ModalDistributionComponent.materialIdOf(item.material?._id))
          .filter((id): id is string => !!id)
      )
    );

    if (materialIds.length === 0) return;

    this.materialService.findToRelated(materialIds).subscribe({
      next: res => {
        const materials = (res?.data ?? []) as unknown as MaterialMasterLike[];
        this.masters = new Map(materials.map(material => [material._id, material]));
        this.applyLogisticUnits();
      },
      error: () => {
        this.handlerMessage.warning({
          message: this.languageService.t('MONITOR_DISTRIBUTION.MASTERS_LOAD_FAILED'),
        });
      },
    });
  }

  /** Adjunta `logisticUnit` (front-only) a cada material agrupado, en las tres señales que lo exponen. */
  private applyLogisticUnits(): void {
    const attach = (item: DistributionGroupedByTrip) => {
      const detail = item.orderdetail?.[0] as unknown as DetailUnitsLike | undefined;
      const materialId = ModalDistributionComponent.materialIdOf(item.material?._id);
      const master = materialId ? this.masters.get(materialId) : undefined;
      item.logisticUnit = detail ? resolveEditableLogisticUnit(master, detail) : null;
    };

    this.deliveryGroupUnified().forEach(doc => (doc.items ?? []).forEach(attach));
    this.deliveryGroupUnified.set([...this.deliveryGroupUnified()]);

    if (this.deliveryGroupMaterialUnified().length > 0) {
      this.deliveryGroupMaterialUnified().forEach(attach);
      this.deliveryGroupMaterialUnified.set([...this.deliveryGroupMaterialUnified()]);
    }

    const expanded = this.expandedMaterial();
    if (expanded) {
      attach(expanded);
      this.expandedMaterial.set({ ...expanded });
    }
  }

  // Ordena documentos y actualiza señal
  sortDocument(deliveryGroupUnified) {
    const sortedData = (deliveryGroupUnified ?? []).sort((a, b) => {
      if (!a.noDto?.serial) return 1;
      if (!b.noDto?.serial) return -1;
      return a.noDto.serial.localeCompare(b.noDto.serial);
    });
    this.deliveryGroupUnified.set(sortedData);
  }

  // Abrir documento
  openDocumentDetail(doc: GroupedBySerial) {
    this.selectedDocument.set(doc);
    this.expandedMaterial.set(null);

    const sortedItems = (doc.items ?? []).sort((a, b) => {
      if (!a.material?.name) return 1;
      if (!b.material?.name) return -1;
      return a.material.name.localeCompare(b.material.name);
    });

    this.deliveryGroupMaterialUnified.set(sortedItems);
    this.drawerOpened = true;
    this.childDrawerOpened = false;
  }

  openDocumentMaterial(mat: DistributionGroupedByTrip) {
    this.expandedMaterial.set(mat);
    this.childDrawerOpened = true;
  }

  closeDrawer() {
    this.drawerOpened = false;
    this.childDrawerOpened = false;
    this.expandedMaterial.set(null);
    this.selectedDocument.set(null);
  }

  closeDrawerChild(event?: boolean) {
    if (!event) {
      this.childDrawerOpened = false;
      this.expandedMaterial.set(null);
    }

    if (event && this.data.tripId) {
      this.fetchOrderGroudMaterial(this.data.tripId);
    }
  }

  onBackdropClicked() {
    this.closeDrawer();
  }
  
  //region action
  // Actualiza los documentos desde el servicio manteniendo selección
  fetchOrderGroudMaterial(id) {
    const currentMaterial = this.expandedMaterial();
    const currentDocumentSerial = this.selectedDocument()?.noDto?.serial;

    this.salesDistributionService.getOrdersGruopByMaterial(id).subscribe({
      next: (res: DistributionGroupedByTrip[]) => {
        const listDataUnified = this.processResponseData(res);
        const groupedData = this.groupBySerial(listDataUnified);

        // Actualizar lista de documentos manteniendo referencias existentes
        this.deliveryGroupUnified.update(oldList => {
          return oldList.map(doc => groupedData.find(d => d.noDto.serial === doc.noDto.serial) ?? doc);
        });

        // Mantener documento seleccionado
        if (currentDocumentSerial) {
          const updatedDoc = groupedData.find(doc => doc.noDto.serial === currentDocumentSerial);
          if (updatedDoc) {
            this.selectedDocument.set(updatedDoc);

            const sortedItems = (updatedDoc.items ?? []).sort((a, b) => a.material?.name?.localeCompare(b.material?.name) ?? 0);
            this.deliveryGroupMaterialUnified.set(sortedItems);

            // Mantener material expandido
            if (currentMaterial) {
              const updatedMaterial = sortedItems.find(mat => mat.material._id === currentMaterial.material._id);
              if (updatedMaterial) this.expandedMaterial.set(updatedMaterial);
            }
          }
        }
      },
      error: error => this.snackBar.open(error.error.message, "Cerrar", { duration: 3000 })
    });
  }

  private processResponseData(res: DistributionGroupedByTrip[]) {
    return res.map(item => {
      // Redondear suma de baseQuantity a 3 decimales
      const totalBaseQuantity = parseFloat(
        item.orderdetail.reduce((sum, i) => sum + (i.baseQuantity || 0), 0).toFixed(3)
      );

      // Redondear diferencia para evitar residuos binarios
      const resultMisSper = parseFloat(
        (item.totalBaseQuantity - totalBaseQuantity).toFixed(3)
      );

      const detail = item.orderdetail?.[0] as unknown as DetailUnitsLike | undefined;
      const materialId = ModalDistributionComponent.materialIdOf(item.material?._id);
      const master = materialId ? this.masters.get(materialId) : undefined;

      return {
        ...item,
        spare: resultMisSper >= 0 ? resultMisSper : 0,
        missing: resultMisSper >= 0 ? 0 : -resultMisSper,
        logisticUnit: detail ? resolveEditableLogisticUnit(master, detail) : null,
      };
    });
  }

  private groupBySerial(data: DistributionGroupedByTrip[]): GroupedBySerial[] {
    const grouped = data.reduce((acc, item) => {
      const key = item.serial;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    }, {});

    return Object.entries(grouped).map(([serial, items]: [string, any[]]) => {
      const totalSpare = items.reduce(
        (sum, item) => sum + (item.spare || 0),
        0
      );
      return {
        noDto: {
          serial,
          code: items[0].externalKeys?.code,
          previousTransactions: items[0].previousTransactions
        },
        totalSpare,
        items,
      };
    });
  }

  trackBySerial(index: number, item: GroupedBySerial) {
    return item.noDto.serial;
  }

  saveMaterialDistribution() {
    if(!this.isSaveDistribution()) return;

    const orderIdList = this.deliveryGroupMaterialUnified().map(item => item._id);
    this.dialogConEditRef = this.dialog.open(this.updateDialog, {
      data: {
        title: "Entregas",
        description: "¿Desea confirmar las entregas?",
      },
      panelClass: "delete-modal",
      disableClose: true,
    });

    this.dialogConEditRef.afterClosed().subscribe(result => {
      if (result) this.fetchupdateOrdeConfirmation({ tripId: this.data.tripId, orderId: orderIdList });
    });
  }

  private fetchupdateOrdeConfirmation(data: { tripId: number; orderId: string[] }) {
    this.updateOrderService.updateOutboundDelivery(data).subscribe({
      next: () => {
        this.snackBar.open("Se guardo exitosamente", "Cerrar", { duration: 3000 });
        this.dialogRef.close('success');
      },
      error: error => this.snackBar.open(error.error.message, "Cerrar", { duration: 3000 })
    });
  }
  //#endregion
}
